import { apiUrl } from "./base";

export interface UtteranceAPI {
  id: string;
  sessionId: string;
  speaker: number;
  text: string;
  startTime?: number;
  endTime?: number;
  confidence?: number;
  language?: string;
  translation?: string;
  createdAt: string;
}

export interface CreateUtteranceRequest {
  sessionId: string;
  speaker: number;
  text: string;
  start?: number;
  end?: number;
  confidence?: number;
  lang?: string;
  translation?: string;
}

export interface UtterancesResponse {
  utterances: UtteranceAPI[];
  count: number;
}

/**
 * Get all utterances for a session
 */
export async function getSessionUtterances(
  sessionId: string
): Promise<UtterancesResponse> {
  const response = await fetch(
    apiUrl(`/api/utterances?sessionId=${encodeURIComponent(sessionId)}`)
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch utterances: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Create a new utterance
 */
export async function createUtterance(
  data: CreateUtteranceRequest
): Promise<UtteranceAPI> {
  const response = await fetch(apiUrl("/api/utterances"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create utterance: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Batch create utterances for performance
 */
export async function batchCreateUtterances(
  sessionId: string,
  utterances: Omit<CreateUtteranceRequest, "sessionId">[]
): Promise<UtterancesResponse> {
  const response = await fetch(
    apiUrl("/api/utterances/batch"),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        sessionId,
        utterances,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to batch create utterances: ${response.statusText}`
    );
  }

  return await response.json();
}

/**
 * Update an existing utterance (mainly for saving translations)
 */
export async function updateUtterance(
  utteranceId: string,
  updates: { translation?: string }
): Promise<UtteranceAPI> {
  const response = await fetch(
    apiUrl(`/api/utterances/${utteranceId}`),
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update utterance: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Delete an utterance
 */
export async function deleteUtterance(id: string): Promise<void> {
  const response = await fetch(apiUrl(`/api/utterances/${id}`), {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error(`Failed to delete utterance: ${response.statusText}`);
  }
}
